import apiService from '../services/apiService';
import Attendance from '../models/Attendance';

export const attendanceController = {
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `attendance?${queryParams}` : 'attendance';
      const response = await apiService.get(endpoint);
      const items = response.data?.map(item => Attendance.fromJSON(item)) || [];
      return { success: true, data: items, total: response.total || items.length };
    } catch (error) {
      return { success: false, data: [], message: error.message || 'Failed to fetch attendance records' };
    }
  },

  getById: async (id) => {
    try {
      const response = await apiService.get(`attendance/${id}`);
      return { success: true, data: Attendance.fromJSON(response.data) };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  },

  getByEmployeeId: async (employeeId, filters = {}) => {
    try {
      const queryParams = new URLSearchParams({ ...filters, employeeId }).toString();
      const endpoint = `attendance?${queryParams}`;
      const response = await apiService.get(endpoint);
      const items = response.data?.map(item => Attendance.fromJSON(item)) || [];
      return { success: true, data: items, total: response.total || items.length };
    } catch (error) {
      return { success: false, data: [], message: error.message };
    }
  },

  create: async (data) => {
    try {
      const item = new Attendance(data);
      const validation = item.validate();
      if (!validation.isValid) return { success: false, errors: validation.errors };
      
      // Calculate work hours if check-in and check-out are provided
      if (item.checkIn && item.checkOut) {
        item.workHours = item.calculateWorkHours();
      }
      
      const response = await apiService.post('attendance/mark', item.toJSON());
      return { success: true, data: Attendance.fromJSON(response.data), message: 'Attendance record created successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  update: async (id, data) => {
    try {
      const item = new Attendance({ ...data, id });
      const validation = item.validate();
      if (!validation.isValid) return { success: false, errors: validation.errors };
      
      // Recalculate work hours if check-in and check-out are provided
      if (item.checkIn && item.checkOut) {
        item.workHours = item.calculateWorkHours();
      }
      
      const response = await apiService.put(`attendance/${id}`, item.toJSON());
      return { success: true, data: Attendance.fromJSON(response.data), message: 'Attendance record updated successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  delete: async (id) => {
    try {
      await apiService.delete(`attendance/${id}`);
      return { success: true, message: 'Attendance record deleted successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  export: async (filters = {}, format = 'csv') => {
    try {
      const queryParams = new URLSearchParams({ ...filters, format }).toString();
      const response = await apiService.get(`attendance/export?${queryParams}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  getStatistics: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `attendance/statistics?${queryParams}` : 'attendance/statistics';
      const response = await apiService.get(endpoint);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  },

  markAttendance: async (employeeId, data) => {
    try {
      const attendanceData = {
        employeeId,
        date: data.date || new Date().toISOString().split('T')[0],
        checkIn: data.checkIn,
        checkOut: data.checkOut,
        status: data.status || 'Present',
        remarks: data.remarks || ''
      };
      
      const item = new Attendance(attendanceData);
      const validation = item.validate();
      if (!validation.isValid) return { success: false, errors: validation.errors };
      
      if (item.checkIn && item.checkOut) {
        item.workHours = item.calculateWorkHours();
      }
      
      const response = await apiService.post('attendance', item.toJSON());
      return { success: true, data: Attendance.fromJSON(response.data), message: 'Attendance marked successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  getTodayAttendance: async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await apiService.get(`attendance?date=${today}`);
      const items = response.data?.map(item => Attendance.fromJSON(item)) || [];
      return { success: true, data: items, total: items.length };
    } catch (error) {
      return { success: false, data: [], message: error.message };
    }
  }
};

export default attendanceController;